import React, { useState } from "react";
import { filterLanded } from "../lib/columnFilter";
import { byUrgencyThenSize } from "../lib/queueOrder";
import { jobsByStatus } from "../lib/selectors";
import { isDriverAssignable } from "../lib/driverDelivery";
import { bySitUrgency } from "../lib/sitLocal";
import { useMcOps } from "../lib/McOpsContext";
import { ColumnFilter, KanbanColumn, StatCard } from "../components/Common";
import { BoardTitleRow } from "../components/JobSearch";

export const LdJobsPage = () => {
  const { state, actions, openDriverAssign } = useMcOps();
  // All / Today / Past 7 days / This week per column, by the day a job landed
  // in it — the same chips as New Daily Assignments.
  const [filters, setFilters] = useState({
    toConfirm: "all",
    transit: "all",
    windowEnding: "all",
    sitLocal: "all",
    sitConfirmed: "all",
    confirmed: "all",
  });
  const filterFor = (column) => (
    <ColumnFilter
      value={filters[column]}
      onChange={(key) => setFilters((prev) => ({ ...prev, [column]: key }))}
    />
  );
  // To Be Confirmed: soonest move first, then the largest job (CF, then price).
  const ldToConfirm = byUrgencyThenSize(filterLanded(jobsByStatus(state, "ld-to-confirm"), filters.toConfirm));
  const ldInTransit = filterLanded(jobsByStatus(state, "ld-in-transit"), filters.transit);
  const ldWindowEnding = filterLanded(jobsByStatus(state, "ld-window-ending"), filters.windowEnding);
  const ldSitLocal = filterLanded(jobsByStatus(state, "ld-warehouse-received"), filters.sitLocal);
  // A job confirmed locally has no landedAt yet: it landed when it was confirmed.
  // Most urgent Operations check-in first (overdue, due today, waiting, crew assigned).
  const ldSitConfirmed = bySitUrgency(filterLanded(
    jobsByStatus(state, "ld-sit-confirmed"),
    filters.sitConfirmed,
    new Date(),
    (j) => j.landedAt || j.sitLocalConfirmedAt,
  ));
  const ldDriver = jobsByStatus(state, "ld-driver-delivery");
  const ldConfirmedWeekly = filterLanded(jobsByStatus(state, "ld-confirmed-weekly"), filters.confirmed);

  // Drag a card onto Driver Deliveries (opens the ETA popup) or from SIT Local
  // onto SIT Local Confirmed.
  const dropJob = (jobId, zone) => {
    const job = state.jobs.find((j) => j.id === jobId);
    if (!job) return;
    if (zone === "driver" && isDriverAssignable(job)) openDriverAssign(jobId);
    else if (zone === "sit" && job.status === "ld-warehouse-received") actions.confirmSitLocal(jobId);
  };

  const reminderCards = state.reminders
    .filter((r) => r.scope === "tomorrow" || r.scope === "today")
    .map((r) => ({
      id: r.id,
      clientName: r.text,
      tags: [r.jobLabel],
      moveFrom: "",
      moveTo: "",
      _isReminder: true,
    }));

  return (
    <div className="page active">
      <BoardTitleRow title="Long Distance Jobs" />

      <div className="counter-row cols-8">
        <StatCard icon="clipboard" color="blue" num={ldToConfirm.length} label="Jobs to Be Confirmed" />
        <StatCard icon="truck" color="amber" num={ldInTransit.length} label="Long Distance In Transit" />
        <StatCard icon="alert" color="red" num={ldWindowEnding.length} label="End of Delivery Window" />
        <StatCard icon="check" color="green" num={ldSitLocal.length} label="SIT Local" />
        <StatCard icon="check" color="blue" num={ldSitConfirmed.length} label="SIT Local Confirmed" />
        <StatCard icon="truck" color="purple" num={ldDriver.length} label="Driver Deliveries" />
        <StatCard icon="clipboard" color="red" num={ldConfirmedWeekly.length} label="Long Distance Confirmed Weekly" />
      </div>

      <div className="kanban cols-8">
        <KanbanColumn
          title="Long Distance — To Be Confirmed"
          jobs={ldToConfirm}
          color="var(--purple)"
          hideCount
          showPriority
          toolbar={filterFor("toConfirm")}
        />
        <KanbanColumn
          title="Long Distance — In Transit"
          jobs={ldInTransit}
          color="var(--warning)"
          tab="s4t"
          hideCount
          toolbar={filterFor("transit")}
        />
        <KanbanColumn
          title="End of Delivery Window"
          jobs={ldWindowEnding}
          color="var(--danger)"
          tab="s4t"
          hideCount
          toolbar={filterFor("windowEnding")}
        />
        <KanbanColumn
          title="SIT Local"
          jobs={ldSitLocal}
          color="var(--cyan)"
          tab="s4t"
          hideCount
          toolbar={filterFor("sitLocal")}
        />
        <KanbanColumn
          title="SIT Local Confirmed"
          jobs={ldSitConfirmed}
          color="var(--primary)"
          tab="s4t"
          hideCount
          toolbar={filterFor("sitConfirmed")}
          dropZone="sit"
          onDropJob={dropJob}
          emptyLabel="Drag a job here from SIT Local once it is confirmed"
        />
        <KanbanColumn
          title="Driver Deliveries"
          jobs={ldDriver}
          color="var(--indigo)"
          tab="s4"
          hideCount
          dropZone="driver"
          onDropJob={dropJob}
          emptyLabel="Drag a job here from In Transit when the Long Distance team shares the delivery ETA"
        />
        <KanbanColumn
          title="Long Distance — Confirmed (Weekly)"
          jobs={ldConfirmedWeekly}
          color="var(--success)"
          hideCount
          toolbar={filterFor("confirmed")}
        />
        <KanbanColumn title="Reminders This Week" jobs={reminderCards} color="var(--ink-400)" hideCount />
      </div>
    </div>
  );
};
